import { useQuery } from '@tanstack/react-query';

import { GET_EFFECTIVE_FEATURE_MAP } from '@app/constants/endpoints';
import { ADMIN_QUERY_KEYS } from '@app/constants/service';
import { fetchData } from '@app/hooks/react-query-helpers';
import queryClient, { type ReadableQueryClient } from '@app/query-client';

export type EffectiveFeatureMap = Record<string, boolean>;

export interface FeatureGateOptions {
  /**
   * If true, treat feature as disabled while feature definitions are loading.
   * Default is fail-open so that features do not flicker during app load.
   */
  failClosed?: boolean;
}

export const effectiveFeaturesQuery = () => ({
  queryKey: ADMIN_QUERY_KEYS.effectiveFeatures(),
  queryFn: () =>
    fetchData(GET_EFFECTIVE_FEATURE_MAP()) as Promise<EffectiveFeatureMap>,
});

export const resolveFeatureEnabled = (
  map: EffectiveFeatureMap | undefined,
  featureName: string,
  { failClosed = false }: FeatureGateOptions = {},
): boolean => {
  const value = map?.[featureName];
  if (value === undefined) {
    return !failClosed;
  }
  return value;
};

/**
 * Org's effective feature gating map (`{ featureName: boolean }`, resolved
 * server-side). Unlike JWT permissions it updates without re-login. The global
 * QueryClient disables mount/focus refetch; re-enabled here (cheap, user-action
 * only) so toggles are picked up on next case open / refocus. No polling —
 * deploys force a refresh and sessions cycle daily.
 */
export const useEffectiveFeatures = () =>
  useQuery({
    ...effectiveFeaturesQuery(),
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  });

/**
 * Whether a feature is effectively enabled for the current org. Defaults to
 * fail-open (enabled when feature definition is loading or absent) so features
 * do not flicker on page load. Pass `{ failClosed: true }` to override.
 *
 * UI/display gate, NOT a security boundary — it's bypassable. Anything that
 * must be *prevented* (compliance/security) must also be enforced server-side.
 */
export const useIsFeatureEnabled = (
  featureName: string,
  options: FeatureGateOptions = {},
): boolean => {
  const { data } = useEffectiveFeatures();
  return resolveFeatureEnabled(data, featureName, options);
};

/**
 * Non-hook version of `useIsFeatureEnabled` for contexts without React (redux,
 * helper services). Relies on a bootstrap prefetch to populate the cache.
 */
export const isFeatureEnabledFromCache = (
  client: ReadableQueryClient | null | undefined,
  featureName: string,
  options: FeatureGateOptions = {},
): boolean => {
  const map = client?.getQueryData?.(ADMIN_QUERY_KEYS.effectiveFeatures()) as
    | EffectiveFeatureMap
    | undefined;
  return resolveFeatureEnabled(map, featureName, options);
};

/**
 * Seeds the effective feature map into the React Query cache at bootstrap for
 * both hook and non-hook consumers. Safe to call once on login alongside the
 * other bootstrap prefetches.
 */
export const prefetchEffectiveFeatures = () =>
  queryClient.prefetchQuery(effectiveFeaturesQuery());
